// Small shared pieces of the Mail workspace (Session AUB): how a sender match, a sender and a free draft's reason read, a
// media query hook for the laptop/phone layouts, and the searchable customer picker the filters and the match control share.
import { useEffect, useState } from "react";
import { RecordPicker } from "../../../components/RecordPicker";
import type { CustomerOption, MailListRow, MailSenderMatch } from "../../../lib/api";

/** How a row's sender match reads, for the list and the card. Display only: it never changes what a draft may say. */
export function matchText(match: MailSenderMatch): { text: string; tone: "strong" | "suggest" | "none" } {
  switch (match.kind) {
    case "confirmed":
    case "exact":
      return { text: match.customerName || "A customer", tone: "strong" };
    case "suggestion":
      return { text: match.candidates.length === 1 ? `Suggested: ${match.candidates[0]!.customerName}?` : "Suggested: several customers?", tone: "suggest" };
    case "ambiguous":
      return { text: "Several customers", tone: "suggest" };
    default:
      return { text: "Unmatched", tone: "none" };
  }
}

/** The customer a match names outright (confirmed by the office, or an exact recorded address), else null. */
export function matchedCustomer(match: MailSenderMatch): { customerId: string; customerName: string; siteId: string | null; siteName: string | null } | null {
  if (match.kind === "confirmed" || match.kind === "exact") {
    return { customerId: match.customerId, customerName: match.customerName, siteId: match.siteId, siteName: match.siteName };
  }
  return null;
}

export function senderText(row: Pick<MailListRow, "from" | "fromName">): string {
  if (row.fromName && row.from) return `${row.fromName} <${row.from}>`;
  return row.fromName || row.from || "Unknown sender";
}

/**
 * Why no template was used for a free draft: the engine's own record (templates/templateEngine.ts TemplateMiss), read
 * defensively because it is stored JSON. Returns the plain-English text and any missing slot names it carries.
 */
export function templateMissParts(miss: unknown): { text: string | null; missingSlots: string[] } {
  if (!miss || typeof miss !== "object") return { text: typeof miss === "string" && miss.trim() ? miss.trim() : null, missingSlots: [] };
  const m = miss as Record<string, unknown>;
  const text = typeof m.text === "string" && m.text.trim() ? m.text.trim() : null;
  const missingSlots = Array.isArray(m.missingSlots) ? m.missingSlots.filter((s): s is string => typeof s === "string" && s.length > 0) : [];
  return { text, missingSlots };
}

export function fmtBytes(n: number | null): string {
  if (n == null) return "size unknown";
  if (n < 1024) return `${n} bytes`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * A media query, re-read when it changes. jsdom (and any browser without matchMedia) answers `fallback`, so component tests
 * render the laptop layout unless they stub matchMedia (the OfficeLayoutDrawer pattern).
 */
export function useMediaQuery(query: string, fallback: boolean): boolean {
  const read = () => (typeof window !== "undefined" && typeof window.matchMedia === "function" ? window.matchMedia(query).matches : fallback);
  const [matches, setMatches] = useState<boolean>(read);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia(query);
    const on = () => setMatches(mql.matches);
    on();
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", on);
      return () => mql.removeEventListener("change", on);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);
  return matches;
}

/** The laptop layout: the list and the review card side by side. */
export const WIDE_QUERY = "(min-width: 1024px)";
/** The phone layout: the list as stacked cards. */
export const PHONE_QUERY = "(max-width: 767px)";

/**
 * A searchable customer picker: type part of a name, tap the customer. `value` is the chosen customer's id (or null).
 */
export function CustomerPicker({
  label,
  value,
  valueName,
  onChange,
  placeholder = "Type a customer's name...",
}: {
  label: string;
  value: string | null;
  valueName?: string | null;
  onChange: (customer: CustomerOption | null) => void;
  placeholder?: string;
}) {
  // Session SC2A: the shared typeahead (search as you type, up to twenty matches) instead of the whole register.
  return (
    <div className="min-w-0">
      <RecordPicker
        kind="customer"
        ariaLabel={label}
        value={value}
        selectedLabel={valueName}
        placeholder={placeholder}
        onChange={(id, rec) => onChange(id && rec ? (rec.raw as unknown as CustomerOption) : null)}
      />
    </div>
  );
}
